import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { COL_HEADER_SX, FILTER_SELECT_SX } from "@/constants/users";
import { usePermissions } from "@/hooks/usePermissions";
import { useRoles } from "@/hooks/useRoles";
import { useCreateUser } from "@/hooks/useUsers";
import { ALL_ROLES, ROLES, type Role } from "@/lib/roles";
import { resolveRoleLabels } from "@/utils/role-label-helper";

interface CreateUserDialogProps {
  open: boolean;
  onClose: () => void;
}

const FIELD_SX = {
  "& .MuiOutlinedInput-root": {
    height: 44,
    borderRadius: "var(--radius-btn)",
    bgcolor: "background.paper",
    fontFamily: "var(--font-lato)",
    fontSize: 14,
    "& fieldset": { borderColor: "var(--color-smoke)" },
    "&:hover fieldset": { borderColor: "var(--color-rodeo-dust)" },
    "&.Mui-focused fieldset": { borderColor: "var(--color-dark-dust)" },
  },
  "& .MuiInputBase-input": {
    fontFamily: "var(--font-lato)",
    fontSize: 14,
    color: "var(--color-rodeo-black)",
  },
};

export default function CreateUserDialog({ open, onClose }: CreateUserDialogProps) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<Role>(ROLES.CLIENT_ADMIN);
  const [error, setError] = useState<string | null>(null);

  const { can, isGfg, organizationId } = usePermissions();
  const { labelFor, roles } = useRoles();
  const canCreateAdmin = can("create", "ADMIN");
  const availableRoles = ALL_ROLES.filter(
    (r) => canCreateAdmin || (r !== ROLES.GFG_SUPER_ADMIN && r !== ROLES.GFG_STANDARD_ADMIN),
  );

  // Two roles can share a label (e.g. VIEW_ONLY and ORG_VIEW_ONLY both read "View Only").
  // Qualify only the labels that actually collide within this dropdown so an unambiguous
  // rename never needs a code change here.
  const displayLabel = resolveRoleLabels(
    availableRoles.map((r) => ({
      key: r,
      label: labelFor(r),
      level: roles.find((option) => option.key === r)?.level,
    })),
  );

  const { mutate: createUser, isPending } = useCreateUser();

  const handleClose = () => {
    setFirstName("");
    setLastName("");
    setEmail("");
    setRole(ROLES.CLIENT_ADMIN);
    setError(null);
    onClose();
  };

  const handleSubmit = () => {
    setError(null);
    createUser(
      {
        email: email.trim(),
        role,
        firstName: firstName.trim(),
        lastName: lastName.trim() || undefined,
        // Client admins send their own org explicitly (guard-validated) rather than relying
        // on a server-side pin. GFG create with an org picker via InviteUserDialog.
        organizationId: !isGfg ? (organizationId ?? undefined) : undefined,
      },
      {
        onSuccess: handleClose,
        onError: (err) => setError(err instanceof Error ? err.message : "Failed to create user."),
      },
    );
  };

  const isValid = email.trim().length > 0 && firstName.trim().length > 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: "var(--radius-card)", boxShadow: "0px 8px 32px rgba(0,0,0,0.12)" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pt: 2.5,
          pb: 2,
          px: 3,
        }}
      >
        <Typography
          component="span"
          variant="subtitle1"
          sx={{ fontWeight: 600, color: "var(--color-rodeo-black)" }}
        >
          Add User
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            border: "1px solid var(--color-smoke)",
            borderRadius: "var(--radius-btn)",
            bgcolor: "background.paper",
            color: "var(--color-graphite)",
            width: 44,
            height: 44,
            "&:hover": { bgcolor: "var(--color-wax)" },
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 3 }}>
        <Stack gap={2}>
          <Stack direction="row" gap={1.5}>
            <Box flex={1}>
              <Typography sx={{ ...COL_HEADER_SX, display: "block", mb: 0.75 }}>
                First Name *
              </Typography>
              <TextField
                fullWidth
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="John"
                sx={FIELD_SX}
              />
            </Box>
            <Box flex={1}>
              <Typography sx={{ ...COL_HEADER_SX, display: "block", mb: 0.75 }}>
                Last Name
              </Typography>
              <TextField
                fullWidth
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Doe"
                sx={FIELD_SX}
              />
            </Box>
          </Stack>

          <Box>
            <Typography sx={{ ...COL_HEADER_SX, display: "block", mb: 0.75 }}>Email *</Typography>
            <TextField
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jane@example.com"
              type="email"
              sx={FIELD_SX}
            />
          </Box>

          <Box>
            <Typography sx={{ ...COL_HEADER_SX, display: "block", mb: 0.75 }}>Role</Typography>
            <Select
              value={role}
              onChange={(e) => setRole(e.target.value as Role)}
              fullWidth
              sx={FILTER_SELECT_SX}
            >
              {availableRoles.map((r) => (
                <MenuItem
                  key={r}
                  value={r}
                  sx={{
                    fontFamily: "var(--font-lato)",
                    fontSize: 14,
                    color: "var(--color-rodeo-black)",
                  }}
                >
                  {displayLabel(r)}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {error && (
            <Typography
              sx={{
                fontSize: 13,
                color: "var(--color-good-wine)",
                bgcolor: "var(--color-good-wine-bg)",
                px: 1.5,
                py: 1,
                borderRadius: "var(--radius-btn)",
              }}
            >
              {error}
            </Typography>
          )}

          <Stack direction="row" justifyContent="space-between" alignItems="center" pt={0.5}>
            <Button
              onClick={handleClose}
              disableRipple
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "1.98px",
                color: "var(--color-dark-dust)",
                textTransform: "uppercase",
                p: 0,
                minWidth: 0,
                "&:hover": { bgcolor: "transparent", color: "var(--color-rodeo-black)" },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isPending || !isValid}
              disableElevation
              sx={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "1.98px",
                borderRadius: "var(--radius-btn)",
                textTransform: "uppercase",
                height: 44,
                bgcolor: "var(--color-good-wine)",
                color: "white",
                "&:hover": { bgcolor: "var(--color-good-wine-2)" },
                "&.Mui-disabled": { bgcolor: "var(--color-smoke)", color: "var(--color-graphite)" },
              }}
            >
              {isPending ? "Inviting…" : "Invite User"}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
